import React from 'react'

import { Skeleton, Tooltip, Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import { CircularProgressbar } from 'react-circular-progressbar'
import { useTranslation } from 'react-i18next'

import { useAppSelector } from '../../../store/hooks'
import { LoadingStatusEnum } from '../../../store/types'
import { SmallWidgetsName, smallWidgetTypes } from '../types'
import { SmallWidget } from '../widgetsComponents'

export const useGetSmallWidgets = () => {
  const { t } = useTranslation()
  const { spacing, palette } = useTheme()

  const { widgetsData, widgetsLoading, customize } = useAppSelector(({ dashboard }) => dashboard)
  const { currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)

  const isLoading = [LoadingStatusEnum.Idle, LoadingStatusEnum.Pending].includes(widgetsLoading)

  return {
    [SmallWidgetsName.grossProfit]: (
      <SmallWidget
        isLoading={isLoading}
        name={SmallWidgetsName.grossProfit}
        label={t('grossProfit', 'Gross Profit (Loss)')}
        mainInfo={widgetsData?.grossProfit ?? '-'}
        currency={currentAccount?.currency ?? ''}
        type={smallWidgetTypes.money}
        explanation={t(
          'grossProfitExplanation',
          'Refers to the amount of money a user earns before subtracting trading commissions, fees, and other expenses, with a positive value indicating earnings and a negative value indicating losses',
        )}
      />
    ),
    [SmallWidgetsName.netProfit]: (
      <SmallWidget
        isLoading={isLoading}
        name={SmallWidgetsName.netProfit}
        label={t('netProfit', 'Net Profit (Loss)')}
        mainInfo={widgetsData?.netProfit ?? '-'}
        currency={currentAccount?.currency ?? ''}
        type={smallWidgetTypes.money}
        explanation={t(
          'netProfitExplanation',
          'Refers to the amount of money a user earns after subtracting trading commissions, fees, and other expenses, with a positive value indicating earnings and a negative value indicating losses',
        )}
      />
    ),
    [SmallWidgetsName.balance]: (
      <SmallWidget
        isLoading={isLoading}
        name={SmallWidgetsName.balance}
        label={t('tradingAccountsPage.accountCard.balance', 'Balance')}
        mainInfo={currentAccount?.balance ?? 0}
        currency={currentAccount?.currency ?? ''}
        type={smallWidgetTypes.money}
        explanation={t(
          'dashboard.balanceExplanation',
          'Broker-reported account balance from the connected trading account.',
        )}
      />
    ),
    [SmallWidgetsName.roi]: (
      <SmallWidget
        isLoading={isLoading}
        name={SmallWidgetsName.roi}
        label={t('roi', 'ROI')}
        mainInfo={widgetsData?.returnOnInvestment}
        type={smallWidgetTypes.percents}
        explanation={t(
          'roiProfitExplanation',
          'Measures the profitability of an investment relative to its cost',
        )}
      />
    ),
    [SmallWidgetsName.avgTradeSize]: (
      <SmallWidget
        isLoading={isLoading}
        name={SmallWidgetsName.avgTradeSize}
        label={t('avgTradeSize')}
        mainInfo={widgetsData?.averageTradeSize}
        currency={currentAccount?.currency ?? ''}
        type={smallWidgetTypes.money}
        explanation={t(
          'averageMoneyValueOfTransactionsExecuted',
          'Average money value of transactions executed',
        )}
      />
    ),
    [SmallWidgetsName.avgHoldingTime]: (
      <SmallWidget
        isLoading={isLoading}
        name={SmallWidgetsName.avgHoldingTime}
        label={t('avgHoldingTime', 'Avg holding time')}
        mainInfo={Number(widgetsData?.averageHoldingTime) ?? 0}
        type={smallWidgetTypes.time}
        explanation={t('averageTimeTraderHoldsPosition', 'Average time trader holds the position')}
      />
    ),
    [SmallWidgetsName.toxicity]: (
      <SmallWidget
        isLoading={isLoading}
        name={SmallWidgetsName.toxicity}
        label={t('toxicity', 'Toxicity')}
        mainInfo={widgetsData?.toxicity?.toxicityPercent}
        type={smallWidgetTypes.percents}
        explanation={t(
          'toxicityProfitExplanation',
          '"Toxicity" refers to a scenario where high transaction costs exceed the potential gains due to closing trades quickly after opening',
        )}
      >
        {isLoading ? (
          <Skeleton width={spacing(7)} height={spacing(7)} variant="circular" />
        ) : (
          <Tooltip
            title={t('toxicTransactions', {
              count: widgetsData?.toxicity?.amountTransactions ?? 0,
            })}
            arrow
            disableHoverListener={customize}
            placement="top"
          >
            <Box width={spacing(7)} height={spacing(7)}>
              <CircularProgressbar
                value={100 - (widgetsData?.toxicity?.toxicityPercent || 0)}
                styles={{
                  path: {
                    stroke: palette.grey[500],
                    strokeLinecap: 'butt',
                    transition: 'stroke-dashoffset 0.5s ease 0s',
                    transformOrigin: 'center center',
                  },
                  trail: {
                    stroke: palette.red[400],
                    strokeLinecap: 'butt',
                    transformOrigin: 'center center',
                  },
                  background: {
                    fill: palette.cyan[500],
                  },
                }}
                strokeWidth={16}
              />
            </Box>
          </Tooltip>
        )}
      </SmallWidget>
    ),
    [SmallWidgetsName.systemQualityNumber]: (
      <SmallWidget
        isLoading={isLoading}
        name={SmallWidgetsName.systemQualityNumber}
        label={t('sqnLabel', 'SQN')}
        mainInfo={widgetsData?.systemQualityNumber}
        type={smallWidgetTypes.number}
        explanation={
          <Typography variant="body1" fontSize={11}>
            {t(
              'sqnDashboardExplanationTitle',
              `The SQN assesses the quality of the trading system. Interpretation:`,
            )}
            <ul>
              <li>{t('sqnDashboardExplanationParam1', '<1.5: Probably Very Hard To Trade')}</li>
              <li>{t('sqnDashboardExplanationParam2', '1.51 to 2.00: Average')}</li>
              <li>{t('sqnDashboardExplanationParam3', '2.01 to 3.00: Good')}</li>
              <li>{t('sqnDashboardExplanationParam4', '3.01 to 5.00: Excellent')}</li>
              <li>{t('sqnDashboardExplanationParam5', '5.01 to 7.00: Super')}</li>
              <li>{t('sqnDashboardExplanationParam6', '7.01 or higher: Holy Grail')}</li>
            </ul>
          </Typography>
        }
      />
    ),
  }
}
